<template>
  <div class="wage-calc-floating">
    <!-- 메인 플로팅 버튼 -->
    <v-btn
      v-if="!isExpanded"
      class="floating-btn"
      color="primary"
      variant="elevated"
      @click="toggleExpanded"
      size="large"
      icon
    >
      <v-icon>mdi-calculator</v-icon>
    </v-btn>

    <!-- 확장된 계산기 패널 -->
    <v-card
      v-if="isExpanded"
      class="calc-panel"
      elevation="8"
      :style="{ height: panelHeight + 'px' }"
    >
      <!-- 크기 조절 핸들 -->
      <div 
        class="resize-handle"
        :class="{ 'resizing': isResizing }"
        @mousedown="startResize"
        title="드래그하여 크기 조절"
      >
        <v-icon size="16">mdi-drag-vertical</v-icon>
      </div>
      
      <!-- 헤더 -->
      <v-card-title class="calc-header">
        <div class="header-content">
          <v-icon class="header-icon">mdi-calculator</v-icon>
          <span class="header-title">급여계산기</span>
          <select 
            v-model="selectedYear" 
            class="year-select"
            @change="onYearChange"
          >
            <option v-for="year in yearOptions" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>
        <v-btn
          icon
          size="small"
          variant="text"
          @click="toggleExpanded"
          class="close-btn"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- 계산기 내용 -->
      <v-card-text class="calc-content">
        <div class="calc-form">
          <!-- 왼쪽: 입력 필드 영역 -->
          <div class="input-section">
            <div class="result-section">
            <div class="section-title">
              <span>시간당 임금</span>
              <span v-if="totalHourlyWage" class="total-amount">{{ totalHourlyWage }}</span>
            </div>
            <!-- 시급 (입력 가능) -->
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">시급</span>
                <span class="result-value">
                  <div class="wage-input-wrapper">
                    <input 
                      type="text" 
                      v-model="hourlyWage" 
                      @focus="onHourlyWageFocus"
                      @blur="onHourlyWageBlur"
                      @input="onHourlyWageInput"
                      @keydown.enter="onHourlyWageEnter"
                      class="wage-input"
                      maxlength="9"
                    />
                    <div class="wage-input-controls">
                      <button 
                        type="button"
                        class="wage-control-btn up"
                        @mousedown="startContinuousIncrement('hourlyWage')"
                        @mouseup="stopContinuousIncrement"
                        @mouseleave="stopContinuousIncrement"
                        title="1원 증가"
                      >
                        <v-icon size="14">mdi-chevron-up</v-icon>
                      </button>
                      <button 
                        type="button"
                        class="wage-control-btn down"
                        @mousedown="startContinuousDecrement('hourlyWage')"
                        @mouseup="stopContinuousIncrement"
                        @mouseleave="stopContinuousIncrement"
                        title="1원 감소"
                      >
                        <v-icon size="14">mdi-chevron-down</v-icon>
                      </button>
                    </div>
                  </div>
                </span>
              </div>
            </div>
            <!-- 주휴수당 (자동 계산, readonly) -->
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">주휴수당</span>
                <span class="result-value">{{ weeklyHolidayAllowance }}</span>
              </div>
            </div>
            <!-- 연차수당 (입력 가능) -->
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">
                  연차수당
                  <button
                    type="button"
                    class="calculate-btn"
                    @click="calculateAnnualLeaveByHourlyWage"
                    title="시급 기준으로 연차수당 계산"
                  >
                    시급기준계산하기
                  </button>
                </span>
                <span class="result-value">
                  <div class="wage-input-wrapper">
                    <input 
                      type="text" 
                      v-model="annualLeaveAllowance"
                      @focus="onAnnualLeaveAllowanceFocus"
                      @blur="onAnnualLeaveAllowanceBlur"
                      @input="onAnnualLeaveAllowanceInput"
                      @keydown.enter="onAnnualLeaveAllowanceEnter"
                      class="wage-input"
                      maxlength="9"
                    />
                    <div class="wage-input-controls">
                      <button 
                        type="button"
                        class="wage-control-btn up"
                        @mousedown="startContinuousIncrement('annualLeave')"
                        @mouseup="stopContinuousIncrement"
                        @mouseleave="stopContinuousIncrement"
                        title="1원 증가"
                      >
                        <v-icon size="14">mdi-chevron-up</v-icon>
                      </button>
                      <button 
                        type="button"
                        class="wage-control-btn down"
                        @mousedown="startContinuousDecrement('annualLeave')"
                        @mouseup="stopContinuousIncrement"
                        @mouseleave="stopContinuousIncrement"
                        title="1원 감소"
                      >
                        <v-icon size="14">mdi-chevron-down</v-icon>
                      </button>
                    </div>
                  </div>
                </span>
              </div>
              <div class="result-item">
            <!-- 근무일수 -->
              <div class="result-row">
                <span class="result-label">근무일수</span>
                <span class="result-value">
                  <div class="wage-input-wrapper">
                    <input 
                      type="text"
                      v-model="workingDaysInput"
                      @input="onWorkingDaysInput"
                      @keydown.enter="onWorkingDaysEnter"
                      class="wage-input"
                      placeholder="근무일수 입력"
                      maxlength="3"
                    />
                    <div class="wage-input-controls">
                      <button 
                        type="button"
                        class="wage-control-btn up"
                        @mousedown="startContinuousIncrement('workingDays')"
                        @mouseup="stopContinuousIncrement"
                        @mouseleave="stopContinuousIncrement"
                        title="1일 증가"
                      >
                        <v-icon size="14">mdi-chevron-up</v-icon>
                      </button>
                      <button 
                        type="button"
                        class="wage-control-btn down"
                        @mousedown="startContinuousDecrement('workingDays')"
                        @mouseup="stopContinuousIncrement"
                        @mouseleave="stopContinuousIncrement"
                        title="1일 감소"
                      >
                        <v-icon size="14">mdi-chevron-down</v-icon>
                      </button>
                    </div>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </div>
          
          <!-- 월 평균 급여 섹션 -->
          <div class="result-section mt-3">
            <div class="section-title">
              <span>월 급여</span>
              <span v-if="basicTotalPay > 0" class="total-amount">{{ formatMoney(basicTotalPay) }}원</span>
            </div>
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">기본급</span>
                <span class="result-value">{{ formatMoney(basicPay) }}원</span>
              </div>
            </div>
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">주휴수당</span>
                <span class="result-value">{{ formatMoney(weeklyHolidayPay) }}원</span>
              </div>
            </div>
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">연차수당</span>
                <span class="result-value">{{ formatMoney(annualLeavePay) }}원</span>
              </div>
            </div>
          </div>
          
          <!-- 월 평균 수가 계산 섹션 -->
          <div class="result-section mt-3">
            <div class="section-title">
              <span>월 수가</span>
              <span v-if="monthlyTotalWage" class="total-amount">{{ monthlyTotalWage }}</span>
            </div>
            
            <!-- 수가 선택 -->
            <div class="result-item">
              <div class="result-row">
                <span class="result-label">시간 선택 (수가)</span>
                <span class="result-value">
                  <select 
                    v-model="selectedSugaTime" 
                    @change="onSugaTimeChange"
                    class="suga-time-select"
                  >
                    <option value="">시간을 선택하세요</option>
                    <option 
                      v-for="suga in sugaList" 
                      :key="suga.suga_idx" 
                      :value="suga.suga_rank"
                    >
                      {{ suga.suga_rank }} - {{ formatMoney(Number(suga.suga_money)) }}원
                    </option>
                  </select>
                </span>
              </div>
            </div>
            
            <!-- 일당 표시 -->
            <div class="result-item" v-if="selectedSugaTime">
              <div class="result-row">
                <span class="result-label">일당</span>
                <span class="result-value">{{ selectedSuga ? formatMoney(Number(selectedSuga.suga_money)) + '원' : '' }}</span>
              </div>
            </div>
      
          </div>
            
          </div>
          
          <!-- 오른쪽: 결과 영역 -->
          <div class="result-section-right" v-if="hourlyWage && parseFloat(String(hourlyWage).replace(/[^\d]/g, '')) > 0">
            
            <!-- 최종 결과 카드 -->
            <v-card class="final-result-card" elevation="3">
              <v-card-title class="card-title">
                <span>최종 결과</span>
              </v-card-title>
              <v-card-text class="card-content">
                <div class="final-result-item">
                  <span class="final-label">월 급여</span>
                  <span class="final-value">{{ formatMoney(basicTotalPay) }}원</span>
                </div>
                <div class="final-result-item deduction">
                  <div class="final-result-item-header" @click="showDetail.employeeDeductions = !showDetail.employeeDeductions">
                    <span class="final-label">근로자 공제</span>
                    <span class="final-value">
                      <v-icon size="16" class="ml-2">{{ showDetail.employeeDeductions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      -{{ formatMoney(totalEmployeeDeductions) }}원
                    </span>
                  </div>
                  <div v-if="showDetail.employeeDeductions" class="deduction-details">
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.nationalPension" class="deduction-checkbox" />
                        국민연금
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(employeeDeductions.nationalPension) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.healthInsurance" class="deduction-checkbox" />
                        건강보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(employeeDeductions.healthInsurance) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.longTermCare" class="deduction-checkbox" />
                        장기요양보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(employeeDeductions.longTermCare) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.employmentInsurance" class="deduction-checkbox" />
                        고용보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(employeeDeductions.employmentInsurance) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.incomeTax" class="deduction-checkbox" />
                        소득세
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(tax.incomeTax) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.localTax" class="deduction-checkbox" />
                        지방세
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(tax.localTax) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyEmployeeDeductions.supportDeduction" class="deduction-checkbox" />
                        지원공제
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(employeeDeductions.supportDeduction) }}원</span>
                    </div>
                  </div>
                </div>
                <div class="final-result-item highlight">
                  <span class="final-label">실수령액</span>
                  <span class="final-value">{{ formatMoney(netPay) }}원</span>
                </div>
                <div class="divider"></div>
                <div class="final-result-item">
                  <span class="final-label">월 수가</span>
                  <span class="final-value">{{ sugaTotalAmount }}</span>
                </div>
                <div class="final-result-item">
                  <span class="final-label">월 급여</span>
                  <span class="final-value" style="color: #dc2626;">-{{ formatMoney(basicTotalPay) }}원</span>
                </div>
                <div class="final-result-item deduction">
                  <div class="final-result-item-header" @click="showDetail.centerDeductions = !showDetail.centerDeductions">
                    <span class="final-label">기관 공제</span>
                    <span class="final-value">
                      <v-icon size="16" class="ml-2">{{ showDetail.centerDeductions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      -{{ formatMoney(totalCenterDeductions) }}원
                    </span>
                  </div>
                  <div v-if="showDetail.centerDeductions" class="deduction-details">
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyCenterDeductions.nationalPension" class="deduction-checkbox" />
                        국민연금
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.nationalPension) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyCenterDeductions.healthInsurance" class="deduction-checkbox" />
                        건강보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.healthInsurance) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyCenterDeductions.longTermCare" class="deduction-checkbox" />
                        장기요양보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.longTermCare) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyCenterDeductions.employmentInsurance" class="deduction-checkbox" />
                        고용보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.employmentInsurance) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyCenterDeductions.unemployment" class="deduction-checkbox" />
                        실업급여
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.unemployment) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <input type="checkbox" v-model="applyCenterDeductions.industrialAccident" class="deduction-checkbox" />
                        산재보험
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.industrialAccident) }}원</span>
                    </div>
                    <div class="deduction-detail-item">
                      <span class="deduction-detail-label">
                        <v-tooltip
                          :disabled="!retirementTooltipMessage || canApplyRetirement"
                          location="top"
                        >
                          <template v-slot:activator="{ props }">
                            <input
                              type="checkbox"
                              v-model="applyCenterDeductions.retirement"
                              :disabled="!canApplyRetirement"
                              class="deduction-checkbox"
                              v-bind="props"
                            />
                          </template>
                          <span style="white-space: pre-line;">{{ retirementTooltipMessage }}</span>
                        </v-tooltip>
                        퇴직금
                      </span>
                      <span class="deduction-detail-value">{{ formatMoney(centerDeductions.retirement) }}원</span>
                    </div>
                  </div>
                </div>
                <div class="final-result-item highlight profit">
                  <span class="final-label">기관 수익</span>
                  <span class="final-value">{{ formatMoney(netProfit) }}원</span>
                </div>
              </v-card-text>
            </v-card>
          </div>
          
          <!-- 기존 월 평균 급여 섹션 (제거 예정) -->
          <div v-if="false && hourlyWage && parseFloat(String(hourlyWage).replace(/[^\d]/g, '')) > 0" class="payroll-calculation-section">
            <!-- 기본 급여 카드 -->
            <v-card class="calculation-card" elevation="2">
              <v-card-title class="card-title">
                <span>월 평균 급여 계산</span>
              </v-card-title>
              <v-card-text class="card-content">
                <div class="result-item">
                  <div class="result-row">
                    <span class="result-label">기본급</span>
                    <span class="result-value">
                      {{ formatMoney(basicPay) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">주휴수당</span>
                    <span class="result-value">
                      {{ formatMoney(weeklyHolidayPay) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">연차수당</span>
                    <span class="result-value">
                      {{ formatMoney(annualLeavePay) }}원
                    </span>
                  </div>
                  <div class="result-row total-row">
                    <span class="result-label">기본급여 합계</span>
                    <span class="result-value">
                      {{ formatMoney(basicTotalPay) }}원
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            
            <!-- 근로자 공제 카드 -->
            <v-card class="calculation-card" elevation="2">
              <v-card-title class="card-title">
                <span>근로자 공제</span>
              </v-card-title>
              <v-card-text class="card-content">
                <div class="result-item">
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.nationalPension" class="deduction-checkbox" />
                      국민연금
                    </span>
                    <span class="result-value">
                      {{ formatMoney(employeeDeductions.nationalPension) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.healthInsurance" class="deduction-checkbox" />
                      건강보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(employeeDeductions.healthInsurance) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.longTermCare" class="deduction-checkbox" />
                      장기요양보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(employeeDeductions.longTermCare) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.employmentInsurance" class="deduction-checkbox" />
                      고용보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(employeeDeductions.employmentInsurance) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.incomeTax" class="deduction-checkbox" />
                      소득세
                    </span>
                    <span class="result-value">
                      {{ formatMoney(tax.incomeTax) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.localTax" class="deduction-checkbox" />
                      지방세
                    </span>
                    <span class="result-value">
                      {{ formatMoney(tax.localTax) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyEmployeeDeductions.supportDeduction" class="deduction-checkbox" />
                      지원공제
                    </span>
                    <span class="result-value">
                      {{ formatMoney(employeeDeductions.supportDeduction) }}원
                    </span>
                  </div>
                  <div class="result-row total-row deduction-row">
                    <span class="result-label">근로자 공제 합계</span>
                    <span class="result-value">
                      {{ formatMoney(totalEmployeeDeductions) }}원
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            
            <!-- 기관 공제 카드 -->
            <v-card class="calculation-card" elevation="2">
              <v-card-title class="card-title">
                <span>기관 공제</span>
              </v-card-title>
              <v-card-text class="card-content">
                <div class="result-item">
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyCenterDeductions.nationalPension" class="deduction-checkbox" />
                      국민연금
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.nationalPension) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyCenterDeductions.healthInsurance" class="deduction-checkbox" />
                      건강보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.healthInsurance) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyCenterDeductions.longTermCare" class="deduction-checkbox" />
                      장기요양보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.longTermCare) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyCenterDeductions.employmentInsurance" class="deduction-checkbox" />
                      고용보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.employmentInsurance) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyCenterDeductions.unemployment" class="deduction-checkbox" />
                      실업급여
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.unemployment) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <input type="checkbox" v-model="applyCenterDeductions.industrialAccident" class="deduction-checkbox" />
                      산재보험
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.industrialAccident) }}원
                    </span>
                  </div>
                  <div class="result-row">
                    <span class="result-label">
                      <v-tooltip
                        :disabled="!retirementTooltipMessage || canApplyRetirement"
                        location="top"
                      >
                        <template v-slot:activator="{ props }">
                          <input
                            type="checkbox"
                            v-model="applyCenterDeductions.retirement"
                            :disabled="!canApplyRetirement"
                            class="deduction-checkbox"
                            v-bind="props"
                          />
                        </template>
                        <span style="white-space: pre-line;">{{ retirementTooltipMessage }}</span>
                      </v-tooltip>
                      퇴직금
                    </span>
                    <span class="result-value">
                      {{ formatMoney(centerDeductions.retirement) }}원
                    </span>
                  </div>
                  <div class="result-row total-row deduction-row">
                    <span class="result-label">기관 공제 합계</span>
                    <span class="result-value">
                      {{ formatMoney(totalCenterDeductions) }}원
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            
          
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- 스낵바 -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="bottom">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import axios from 'axios';

// 상태 관리
const isExpanded = ref(false);
const currentYear = new Date().getFullYear();
const selectedYear = ref(currentYear);

// 패널 크기 조절
const panelHeight = ref(600); // 기본 높이
const isResizing = ref(false);
const resizeStartY = ref(0);
const resizeStartHeight = ref(0);

// 연도 옵션 (현재 연도 ±1년)
const yearOptions = ref([
  currentYear - 1,
  currentYear,
  currentYear + 1
]);

// 원단위 절삭 함수 (예: 51131 -> 51130)
const truncateToTen = (value: number) => Math.floor(value / 10) * 10;

// 세금 데이터
const taxData = ref({}) as any;
const sojiData = ref({}) as any;

// 공휴일 데이터
const holidaysData = ref<any[]>([]);

// 수가 데이터
const sugaList = ref<any[]>([]);
const selectedSugaTime = ref<string>('');

// 선택된 수가 정보
const selectedSuga = computed(() => {
  if (!selectedSugaTime.value) return null;
  return sugaList.value.find((suga: any) => suga.suga_rank === selectedSugaTime.value);
});

// 급여 계산 데이터
const hourlyWage = ref(''); // 사용자가 입력하는 시급 (표시용, 포맷된 문자열)
const calculatedHourlyWage = ref(0); // 계산된 시급 (계산용, 숫자)
const weeklyHolidayAllowance = ref(''); // 자동 계산된 주휴수당 (시급의 20%)
const annualLeaveAllowance = ref(''); // 사용자가 입력하는 연차수당 (표시용, 포맷된 문자열)
const calculatedAnnualLeaveAllowance = ref(0); // 계산된 연차수당 (계산용, 숫자)
// 사용자 입력 근무일수
const workingDaysInput = ref<string>('');

// 상세보기 상태
const showDetail = ref({
  employeeDeductions: false, // 근로자 공제 접기/펼치기
  centerDeductions: false // 기관 공제 접기/펼치기
});

// 근로자 공제 체크박스 상태
const applyEmployeeDeductions = ref({
  nationalPension: true,
  healthInsurance: true,
  longTermCare: true,
  employmentInsurance: true,
  incomeTax: true,
  localTax: true,
  supportDeduction: true
});

// 기관 공제 체크박스 상태
const applyCenterDeductions = ref({
  nationalPension: true,
  healthInsurance: true,
  longTermCare: true,
  employmentInsurance: true,
  unemployment: true,
  industrialAccident: true,
  retirement: true
});

// 연차수당 계산을 위한 데이터
const vacationDays = ref('11');
const dailyWorkHours = ref('8');
const vacationYear = ref(2); // 연차차수 (1년차, 2년차 등)

// 근로기준법에 따른 연차차수 옵션
const vacationYearOptions = ref([
  { value: 1, label: '1년차', days: 11 },
  { value: 2, label: '2년차', days: 15 },
  { value: 3, label: '3년차', days: 15 },
  { value: 4, label: '4년차', days: 16 },
  { value: 5, label: '5년차', days: 16 },
  { value: 6, label: '6년차', days: 17 },
  { value: 7, label: '7년차', days: 17 },
  { value: 8, label: '8년차', days: 18 },
  { value: 9, label: '9년차', days: 18 },
  { value: 10, label: '10년차', days: 19 },
  { value: 10, label: '11년차', days: 19 },
  { value: 10, label: '12년차', days: 20 },
  { value: 10, label: '13년차', days: 20 },
  { value: 10, label: '14년차', days: 21 },
  { value: 10, label: '15년차', days: 21 },
  { value: 10, label: '16년차', days: 22 },
  { value: 10, label: '17년차', days: 22 },
  { value: 10, label: '18년차', days: 23 },
  { value: 10, label: '19년차', days: 23 },
  { value: 10, label: '20년차', days: 24 },
  { value: 10, label: '21년차이상', days: 25 },
]);

// 더 이상 사용되지 않는 연차차수 관련 함수 제거

// 스낵바
const snackbar = ref({
  show: false,
  message: "",
  color: "error"
});

// 스낵바 표시 함수
const showSnackbar = (message: string, color: string = "error") => {
  snackbar.value = {
    show: true,
    message,
    color
  };
};

// 세금 데이터 가져오기 함수
const getTax = async () => {
  try {
    const year = selectedYear.value.toString();
    const response = await axios.get(`/api/calc/tax/${year}`);
    if (response.data.success) {
      taxData.value = response.data.data.taxRate;
      sojiData.value = response.data.data.soji || [];
      if(!taxData.value){
        selectedYear.value = currentYear;
        showSnackbar("해당 연도로 변경할 수 없습니다");
        getTax();
      } else {
        // 최저시급 기반으로 초기값 설정
        const minWage = taxData.value.tr_time_pay || 0;
        if (minWage > 0) {
          // 시급 초기값을 최저시급으로 설정
          hourlyWage.value = new Intl.NumberFormat('ko-KR').format(minWage);
          calculatedHourlyWage.value = minWage;
          // 주휴수당 자동 계산
          calculateWeeklyHolidayAllowance();
          // 연차수당 초기값을 시급 기반으로 계산
          // 계산 공식: 시급 × (15/12) ÷ 근무일수(20일 기본값)
          const defaultWorkingDays = 20;
          const calculatedAnnualLeave = Math.round(minWage * (15 / 12) / defaultWorkingDays);
          annualLeaveAllowance.value = new Intl.NumberFormat('ko-KR').format(calculatedAnnualLeave);
          calculatedAnnualLeaveAllowance.value = calculatedAnnualLeave;
        }
      }
    }
  } catch (error: any) {
    console.error("세액 조회 오류:", error);
  }
};

// 시급과 연차수당 기반으로 주휴수당 자동 계산
const calculateWeeklyHolidayAllowance = () => {
  const numericHourlyWage = parseFloat(String(hourlyWage.value).replace(/[^\d]/g, '')) || 0;
  if (numericHourlyWage > 0) {
    const calculatedWeeklyHoliday = Math.round(numericHourlyWage * 0.2);
    weeklyHolidayAllowance.value = new Intl.NumberFormat('ko-KR').format(calculatedWeeklyHoliday);
  } else {
    weeklyHolidayAllowance.value = '';
  }
};

// 연도 변경 핸들러
const onYearChange = async () => {
  calculatedHourlyWage.value = 0; // 계산된 시급 초기화
  calculatedAnnualLeaveAllowance.value = 0; // 계산된 연차수당 초기화
  await getTax();
  await loadHolidays();
  await loadSuga();
  // loadSuga에서 자동으로 '180분'을 기본값으로 설정함
};

// 공휴일 데이터 가져오기
const loadHolidays = async () => {
  try {
    const year = selectedYear.value.toString();
    const response = await axios.get(`/api/calc/holidays/${year}`);
    if (response.data.success) {
      holidaysData.value = response.data.data || [];
    }
  } catch (error: any) {
    console.error("공휴일 조회 오류:", error);
  }
};

// 수가 데이터 가져오기
const loadSuga = async () => {
  try {
    const year = selectedYear.value.toString();
    const response = await axios.get(`/api/calc/suga/${year}`);
    if (response.data.success) {
      sugaList.value = response.data.data || [];
      // '180분' 항목을 기본값으로 설정
      const defaultSuga = sugaList.value.find((suga: any) => suga.suga_rank === '180분');
      if (defaultSuga) {
        selectedSugaTime.value = defaultSuga.suga_rank;
      }
    }
  } catch (error: any) {
    console.error("수가 조회 오류:", error);
  }
};

// 최종 근무일수 (사용자 입력, 기본값 20일)
const workingDaysFinal = computed(() => {
  const inputStr = String(workingDaysInput.value || '').trim();
  // 입력이 완전히 비어있으면 디폴트 20일
  if (inputStr === '') {
    return 20;
  }
  // 숫자 파싱 (0 포함)
  const v = parseInt(inputStr.replace(/[^\d]/g, ''));
  // 0 이상의 유효한 숫자면 그 값 사용, 아니면 디폴트 20일
  return Number.isFinite(v) && v >= 0 ? v : 20;
});

// 수가 시간을 시간 단위로 변환하는 함수 (예: "180분" -> 3시간)
const parseSugaTimeToHours = (sugaRank: string): number => {
  if (!sugaRank) return 0;
  
  // "180분", "240분" 같은 형식에서 숫자만 추출
  const minutes = parseFloat(String(sugaRank).replace(/[^\d.-]/g, '')) || 0;
  
  // 분을 시간으로 변환
  return minutes / 60;
};

// 퇴직금 적용 가능 여부 계산 (근무일수 * 수가시간 >= 60시간)
const canApplyRetirement = computed(() => {
  if (!selectedSuga.value || !selectedSuga.value.suga_rank) {
    return false;
  }
  
  const sugaHours = parseSugaTimeToHours(selectedSuga.value.suga_rank);
  const totalHours = workingDaysFinal.value * sugaHours;
  
  return totalHours >= 60;
});

// 퇴직금 체크박스 비활성화 메시지
const retirementTooltipMessage = computed(() => {
  if (!selectedSuga.value || !selectedSuga.value.suga_rank) {
    return '수가 시간을 선택해주세요';
  }
  
  const sugaHours = parseSugaTimeToHours(selectedSuga.value.suga_rank);
  const totalHours = workingDaysFinal.value * sugaHours;
  
  if (totalHours < 60) {
    return `월 수가시간이 60시간 미만입니다.\n(현재: ${totalHours.toFixed(1)}시간, 필요: 60시간 이상)`;
  }
  
  return '';
});

// 시급 입력 필드 포커스 이벤트 (컴마 제거)
const onHourlyWageFocus = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  // 컴마 제거하여 숫자만 표시
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  hourlyWage.value = numericValue;
};

// 시급 입력 필드 포커스 아웃 이벤트 (검증 및 컴마 추가)
const onHourlyWageBlur = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  
  // 숫자만 추출
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const numeric = parseInt(numericValue);
    const minWage = taxData.value?.tr_time_pay || 0;
    
    // 최저시급 이하인 경우 경고 후 최저시급으로 복원
    if (minWage > 0 && numeric < minWage) {
      showSnackbar(`시급은 최소 ${new Intl.NumberFormat('ko-KR').format(minWage)}원 이상이어야 합니다.`, 'error');
      const formattedValue = new Intl.NumberFormat('ko-KR').format(minWage);
      input.value = formattedValue;
      hourlyWage.value = formattedValue;
      calculatedHourlyWage.value = minWage;
      calculateWeeklyHolidayAllowance();
      return;
    }
    
    // 천원단위로 컴마 추가
    const formattedValue = new Intl.NumberFormat('ko-KR').format(numeric);
    input.value = formattedValue;
    hourlyWage.value = formattedValue;
    calculatedHourlyWage.value = numeric;
    
    // 주휴수당 자동 계산
    calculateWeeklyHolidayAllowance();
  } else {
    // 빈 값이면 최저시급으로 설정
    const minWage = taxData.value?.tr_time_pay || 0;
    if (minWage > 0) {
      const formattedValue = new Intl.NumberFormat('ko-KR').format(minWage);
      input.value = formattedValue;
      hourlyWage.value = formattedValue;
      calculatedHourlyWage.value = minWage;
      calculateWeeklyHolidayAllowance();
    }
  }
};

// 시급 입력 필드 입력 이벤트 (숫자만 허용)
const onHourlyWageInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  
  // 숫자가 아닌 문자 제거
  value = value.replace(/[^\d]/g, '');
  
  // 최대 9자리까지 제한
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  
  input.value = value;
  hourlyWage.value = value;
};

// 연차수당 입력 필드 포커스 이벤트 (컴마 제거)
const onAnnualLeaveAllowanceFocus = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  // 컴마 제거하여 숫자만 표시
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  annualLeaveAllowance.value = numericValue;
};

// 연차수당 입력 필드 포커스 아웃 이벤트 (검증 및 컴마 추가)
const onAnnualLeaveAllowanceBlur = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  
  // 숫자만 추출
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const numeric = parseInt(numericValue);
    
    // 천원단위로 컴마 추가
    const formattedValue = new Intl.NumberFormat('ko-KR').format(numeric);
    input.value = formattedValue;
    annualLeaveAllowance.value = formattedValue;
    calculatedAnnualLeaveAllowance.value = numeric;
  } else {
    // 빈 값이면 0으로 설정
    input.value = '0';
    annualLeaveAllowance.value = '0';
    calculatedAnnualLeaveAllowance.value = 0;
  }
};

// 연차수당 입력 필드 입력 이벤트 (숫자만 허용)
const onAnnualLeaveAllowanceInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  
  // 숫자가 아닌 문자 제거
  value = value.replace(/[^\d]/g, '');
  
  // 최대 9자리까지 제한
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  
  input.value = value;
  annualLeaveAllowance.value = value;
};

// 시급 기반 연차수당 계산 함수
const calculateAnnualLeaveByHourlyWage = () => {
  const numericHourlyWage = parseFloat(String(hourlyWage.value).replace(/[^\d]/g, '')) || 0;
  
  if (numericHourlyWage <= 0) {
    showSnackbar('시급을 먼저 입력해주세요.', 'error');
    return;
  }
  
  if (workingDaysFinal.value <= 0) {
    showSnackbar('근무일수를 먼저 입력해주세요.', 'error');
    return;
  }
  
  // 연차수당 계산: 시급 * (15/12) / 근로일수
  const calculatedValue = Math.round(numericHourlyWage * (15 / 12) / workingDaysFinal.value);
  
  // 포맷팅하여 입력 필드에 설정
  const formattedValue = new Intl.NumberFormat('ko-KR').format(calculatedValue);
  annualLeaveAllowance.value = formattedValue;
  calculatedAnnualLeaveAllowance.value = calculatedValue;
  
  showSnackbar('연차수당이 계산되었습니다.', 'success');
};

// 시급 입력 필드 엔터 키 이벤트 (포커스 아웃)
const onHourlyWageEnter = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement;
  input.blur();
};

// 연차수당 입력 필드 엔터 키 이벤트 (포커스 아웃)
const onAnnualLeaveAllowanceEnter = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement;
  input.blur();
};

// 근무일수 입력 필드 엔터 키 이벤트 (포커스 아웃)
const onWorkingDaysEnter = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement;
  input.blur();
};

// 시급 1원 증가
const incrementHourlyWage = () => {
  const numeric = parseFloat(String(hourlyWage.value).replace(/[^\d]/g, '')) || 0;
  const newValue = numeric + 1;
  hourlyWage.value = new Intl.NumberFormat('ko-KR').format(newValue);
  calculatedHourlyWage.value = newValue;
  calculateWeeklyHolidayAllowance();
};

// 시급 1원 감소
const decrementHourlyWage = () => {
  const numeric = parseFloat(String(hourlyWage.value).replace(/[^\d]/g, '')) || 0;
  const minWage = taxData.value?.tr_time_pay || 0;
  if (numeric > minWage) {
    const newValue = numeric - 1;
    hourlyWage.value = new Intl.NumberFormat('ko-KR').format(newValue);
    calculatedHourlyWage.value = newValue;
    calculateWeeklyHolidayAllowance();
  } else {
    showSnackbar(`시급은 최소 ${new Intl.NumberFormat('ko-KR').format(minWage)}원 이상이어야 합니다.`, 'error');
  }
};

// 연차수당 1원 증가
const incrementAnnualLeaveAllowance = () => {
  const numeric = parseFloat(String(annualLeaveAllowance.value).replace(/[^\d]/g, '')) || 0;
  const newValue = numeric + 1;
  annualLeaveAllowance.value = new Intl.NumberFormat('ko-KR').format(newValue);
  calculatedAnnualLeaveAllowance.value = newValue;
};

// 연차수당 1원 감소
const decrementAnnualLeaveAllowance = () => {
  const numeric = parseFloat(String(annualLeaveAllowance.value).replace(/[^\d]/g, '')) || 0;
  if (numeric > 0) {
    const newValue = numeric - 1;
    annualLeaveAllowance.value = new Intl.NumberFormat('ko-KR').format(newValue);
    calculatedAnnualLeaveAllowance.value = newValue;
  }
};

// 근무일수 1일 증가
const incrementWorkingDays = () => {
  const numeric = parseInt(String(workingDaysInput.value).replace(/[^\d]/g, '')) || 0;
  const newValue = numeric + 1;
  workingDaysInput.value = String(newValue);
};

// 근무일수 1일 감소
const decrementWorkingDays = () => {
  const numeric = parseInt(String(workingDaysInput.value).replace(/[^\d]/g, '')) || 0;
  if (numeric > 0) {
    const newValue = numeric - 1;
    workingDaysInput.value = String(newValue);
  }
};

// 연속 실행 관련 변수
let continuousTimer: ReturnType<typeof setTimeout> | null = null;
let continuousInterval: ReturnType<typeof setInterval> | null = null;
let continuousType: 'hourlyWage' | 'annualLeave' | 'workingDays' | null = null;
let continuousDirection: 'increment' | 'decrement' | null = null;
let mousedownTime: number = 0;
let hasExecutedOnce: boolean = false;

// 연속 증가 시작
const startContinuousIncrement = (type: 'hourlyWage' | 'annualLeave' | 'workingDays') => {
  // 기존 타이머/인터벌 정리
  stopContinuousIncrement();
  
  mousedownTime = Date.now();
  hasExecutedOnce = false;
  continuousType = type;
  continuousDirection = 'increment';
  
  // 0.4초 후 연속 실행 시작
  continuousTimer = setTimeout(() => {
    if (continuousType && continuousDirection === 'increment') {
      // 첫 실행
      if (!hasExecutedOnce) {
        if (continuousType === 'hourlyWage') {
          incrementHourlyWage();
        } else if (continuousType === 'annualLeave') {
          incrementAnnualLeaveAllowance();
        } else if (continuousType === 'workingDays') {
          incrementWorkingDays();
        }
        hasExecutedOnce = true;
      }
      
      // 100ms마다 실행 (적당한 속도)
      continuousInterval = setInterval(() => {
        if (continuousType === 'hourlyWage') {
          incrementHourlyWage();
        } else if (continuousType === 'annualLeave') {
          incrementAnnualLeaveAllowance();
        } else if (continuousType === 'workingDays') {
          incrementWorkingDays();
        }
      }, 100);
    }
  }, 400);
};

// 연속 감소 시작
const startContinuousDecrement = (type: 'hourlyWage' | 'annualLeave' | 'workingDays') => {
  // 기존 타이머/인터벌 정리
  stopContinuousIncrement();
  
  mousedownTime = Date.now();
  hasExecutedOnce = false;
  continuousType = type;
  continuousDirection = 'decrement';
  
  // 0.4초 후 연속 실행 시작
  continuousTimer = setTimeout(() => {
    if (continuousType && continuousDirection === 'decrement') {
      // 첫 실행
      if (!hasExecutedOnce) {
        if (continuousType === 'hourlyWage') {
          decrementHourlyWage();
        } else if (continuousType === 'annualLeave') {
          decrementAnnualLeaveAllowance();
        } else if (continuousType === 'workingDays') {
          decrementWorkingDays();
        }
        hasExecutedOnce = true;
      }
      
      // 100ms마다 실행 (적당한 속도)
      continuousInterval = setInterval(() => {
        if (continuousType === 'hourlyWage') {
          decrementHourlyWage();
        } else if (continuousType === 'annualLeave') {
          decrementAnnualLeaveAllowance();
        } else if (continuousType === 'workingDays') {
          decrementWorkingDays();
        }
      }, 100);
    }
  }, 400);
};

// 연속 실행 정지
const stopContinuousIncrement = () => {
  const mouseupTime = Date.now();
  const elapsed = mouseupTime - mousedownTime;
  
  // 0.4초 이내에 mouseup이 발생하면 한 번만 실행 (클릭)
  if (elapsed < 400 && !hasExecutedOnce && continuousType && continuousDirection) {
    if (continuousDirection === 'increment') {
      if (continuousType === 'hourlyWage') {
        incrementHourlyWage();
      } else if (continuousType === 'annualLeave') {
        incrementAnnualLeaveAllowance();
      } else if (continuousType === 'workingDays') {
        incrementWorkingDays();
      }
    } else if (continuousDirection === 'decrement') {
      if (continuousType === 'hourlyWage') {
        decrementHourlyWage();
      } else if (continuousType === 'annualLeave') {
        decrementAnnualLeaveAllowance();
      } else if (continuousType === 'workingDays') {
        decrementWorkingDays();
      }
    }
  }
  
  if (continuousTimer) {
    clearTimeout(continuousTimer);
    continuousTimer = null;
  }
  if (continuousInterval) {
    clearInterval(continuousInterval);
    continuousInterval = null;
  }
  continuousType = null;
  continuousDirection = null;
  hasExecutedOnce = false;
};

// 상세보기 토글 함수
const toggleDetail = (type: string) => {
  showDetail.value[type as keyof typeof showDetail.value] = !showDetail.value[type as keyof typeof showDetail.value];
};

// 기존 직접입력 및 연차수당 계산 함수 제거 (더 이상 필요 없음)

// 근무일수 입력 이벤트
const onWorkingDaysInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  
  // 숫자만 허용
  value = value.replace(/[^\d]/g, '');
  
  // 최대 3자리까지 제한
  if (value.length > 3) {
    value = value.slice(0, 3);
  }
  
  input.value = value;
  workingDaysInput.value = value;
  // workingDaysFinal이 computed로 자동 업데이트되고, 연관된 모든 값들이 자동 계산됨
};

// 합산금액 계산 (시급 + 주휴수당 + 연차수당)
const totalHourlyWage = computed(() => {
  const numericHourlyWage = parseFloat(String(hourlyWage.value).replace(/[^\d]/g, '')) || 0;
  const numericWeeklyHoliday = parseFloat(String(weeklyHolidayAllowance.value).replace(/[^\d]/g, '')) || 0;
  const numericAnnualLeave = parseFloat(String(annualLeaveAllowance.value).replace(/[^\d]/g, '')) || 0;
  
  const total = numericHourlyWage + numericWeeklyHoliday + numericAnnualLeave;
  
  if (total > 0) {
    return new Intl.NumberFormat('ko-KR').format(Math.round(total)) + '원';
  }
  return '';
});

// 수가 시간 선택 변경 이벤트
const onSugaTimeChange = () => {
  // 수가 선택 시 필요한 추가 처리가 있으면 여기에 구현
};

// 월 총 임금 계산 (수가 금액 × 근무일수)
const monthlyTotalWage = computed(() => {
  if (!selectedSuga.value || !selectedSuga.value.suga_money) return '';
  const sugaMoney = Number(selectedSuga.value.suga_money);
  if (sugaMoney > 0 && workingDaysFinal.value > 0) {
    return new Intl.NumberFormat('ko-KR').format(Math.round(sugaMoney * workingDaysFinal.value)) + '원';
  }
  return '';
});

// 수가총액 계산 (수가 금액 × 근무일수) - 포맷된 문자열
const sugaTotalAmount = computed(() => {
  if (!selectedSuga.value || !selectedSuga.value.suga_money) return '';
  const sugaMoney = Number(selectedSuga.value.suga_money);
  if (sugaMoney > 0 && workingDaysFinal.value > 0) {
    return formatMoney(Math.round(sugaMoney * workingDaysFinal.value)) + '원';
  }
  return '';
});

// 수가총액 계산 (수가 금액 × 근무일수) - 숫자 버전 (계산용)
const sugaTotalAmountNumeric = computed(() => {
  if (!selectedSuga.value || !selectedSuga.value.suga_money) return 0;
  const sugaMoney = Number(selectedSuga.value.suga_money);
  if (sugaMoney > 0 && workingDaysFinal.value > 0) {
    return Math.round(sugaMoney * workingDaysFinal.value);
  }
  return 0;
});

// 순수익 계산 (월 수가 - 월 급여 - 기관 공제)
const netProfit = computed(() => {
  return Math.max(0, sugaTotalAmountNumeric.value - basicTotalPay.value - totalCenterDeductions.value);
});

// 월 평균 임금 계산 (월 총 임금 / 근무일수)

// 월 수가시간 계산 (수가시간 × 근무일수)
const totalSugaHours = computed(() => {
  if (!selectedSuga.value || !selectedSuga.value.suga_rank) return 0;
  const sugaHours = parseSugaTimeToHours(selectedSuga.value.suga_rank);
  return sugaHours * workingDaysFinal.value;
});

// 실제 사용할 시급 계산 (계산된 시급 사용, 없으면 최저시급)
const actualTimePay = computed(() => {
  return calculatedHourlyWage.value > 0 ? calculatedHourlyWage.value : (taxData.value?.tr_time_pay || 0);
});

// 시급 입력값 변경 감지하여 주휴수당 자동 재계산
watch(() => hourlyWage.value, () => {
  // 입력 중에는 재계산하지 않음 (blur 시에만 재계산)
}, { deep: true });

// 기본급 계산 (PgmPayroll.vue 방식: 시급 * suga_hours)
const basicPay = computed(() => {
  if (actualTimePay.value === 0 || totalSugaHours.value === 0) return 0;
  return Math.round(actualTimePay.value * totalSugaHours.value);
});

// 주휴수당 계산 (PgmPayroll.vue 방식: 시급 * suga_hours * 0.2)
const weeklyHolidayPay = computed(() => {
  if (actualTimePay.value === 0 || totalSugaHours.value === 0) return 0;
  return Math.round(actualTimePay.value * totalSugaHours.value * 0.2);
});

// 연차수당 계산 (시간당 연차수당 * 월 수가시간)
const annualLeavePay = computed(() => {
  if (totalSugaHours.value === 0) return 0;
  
  // 시간당 연차수당 가져오기 (사용자가 입력한 값)
  const numericAnnualLeave = calculatedAnnualLeaveAllowance.value || 0;
  
  if (numericAnnualLeave > 0) {
    // 시간당 연차수당 * 월 수가시간 = 월간 연차수당
    return Math.round(numericAnnualLeave * totalSugaHours.value);
  }
  
  return 0;
});

// 기본급여 합계 (기본급 + 주휴수당 + 연차수당)
const basicTotalPay = computed(() => {
  return basicPay.value + weeklyHolidayPay.value + annualLeavePay.value;
});

// 과세 급여 (기본급여 합계)
const taxablePay = computed(() => {
  return basicTotalPay.value;
});

// 소득세 계산 함수
const calculateIncomeTax = (taxableIncome: number) => {
  if (!sojiData.value || sojiData.value.length === 0) {
    return 0;
  }
  
  for (let i = 0; i < sojiData.value.length; i++) {
    const obj = sojiData.value[i];
    const minIncome = obj.income1 * 1000;
    const maxIncome = obj.income2 * 1000;
    
    if (taxableIncome >= minIncome && taxableIncome < maxIncome) {
      return Math.round(obj.depe_1);
    }
  }
  
  return 0;
};

// 근로자 공제 계산
const employeeDeductions = computed(() => {
  if (!taxData.value || !taxData.value.tr_time_pay) {
    return {
      nationalPension: 0,
      healthInsurance: 0,
      employmentInsurance: 0,
      longTermCare: 0,
      supportDeduction: 0
    };
  }
  
  const calculatedNationalPension = truncateToTen(taxablePay.value * taxData.value.tr_yeon);
  const calculatedHealthInsurance = truncateToTen(taxablePay.value * taxData.value.tr_gun);
  const calculatedEmploymentInsurance = truncateToTen(taxablePay.value * taxData.value.tr_go);
  const calculatedLongTermCare = truncateToTen(calculatedHealthInsurance * taxData.value.tr_jang);
  
  // 지원공제는 장기근속수당이 있는 경우에만 계산 (현재는 0으로 설정)
  const calculatedSupportDeduction = 0;
  
  return {
    nationalPension: applyEmployeeDeductions.value.nationalPension ? calculatedNationalPension : 0,
    healthInsurance: applyEmployeeDeductions.value.healthInsurance ? calculatedHealthInsurance : 0,
    employmentInsurance: applyEmployeeDeductions.value.employmentInsurance ? calculatedEmploymentInsurance : 0,
    longTermCare: applyEmployeeDeductions.value.longTermCare ? calculatedLongTermCare : 0,
    supportDeduction: applyEmployeeDeductions.value.supportDeduction ? calculatedSupportDeduction : 0
  };
});

// 기관 공제 계산 (사업자 부담분)
const centerDeductions = computed(() => {
  if (!taxData.value || !taxData.value.tr_time_pay) {
    return {
      nationalPension: 0,
      healthInsurance: 0,
      employmentInsurance: 0,
      longTermCare: 0,
      unemployment: 0,
      industrialAccident: 0,
      retirement: 0
    };
  }
  
  // 사업자 부담분은 근로자와 동일한 비율로 계산 (일반적으로 50:50이지만, 일부는 다를 수 있음)
  // 여기서는 근로자와 동일한 비율로 계산하되, 실제로는 사업자 부담 비율이 다를 수 있음
  const calculatedNationalPension = truncateToTen(taxablePay.value * taxData.value.tr_yeon);
  const calculatedHealthInsurance = truncateToTen(taxablePay.value * taxData.value.tr_gun);
  const calculatedEmploymentInsurance = truncateToTen(taxablePay.value * taxData.value.tr_go);
  const calculatedLongTermCare = truncateToTen(calculatedHealthInsurance * taxData.value.tr_jang);
  const calculatedUnemployment = truncateToTen(taxablePay.value * taxData.value.tr_sil);
  const calculatedIndustrialAccident = truncateToTen(taxablePay.value * taxData.value.tr_san);
  
  // 퇴직금 = 세전 총 월급여(기본급 + 주휴수당 + 연차수당) / 12 (체크박스가 체크되어 있을 때만)
  let calculatedRetirement = 0;
  if (applyCenterDeductions.value.retirement) {
    calculatedRetirement = truncateToTen(basicTotalPay.value / 12);
  }
  
  return {
    nationalPension: applyCenterDeductions.value.nationalPension ? calculatedNationalPension : 0,
    healthInsurance: applyCenterDeductions.value.healthInsurance ? calculatedHealthInsurance : 0,
    employmentInsurance: applyCenterDeductions.value.employmentInsurance ? calculatedEmploymentInsurance : 0,
    longTermCare: applyCenterDeductions.value.longTermCare ? calculatedLongTermCare : 0,
    unemployment: applyCenterDeductions.value.unemployment ? calculatedUnemployment : 0,
    industrialAccident: applyCenterDeductions.value.industrialAccident ? calculatedIndustrialAccident : 0,
    retirement: calculatedRetirement
  };
});

// 세금 계산
const tax = computed(() => {
  const calculatedIncomeTax = calculateIncomeTax(taxablePay.value);
  const calculatedLocalTax = Math.round(calculatedIncomeTax * 0.1);
  
  return {
    incomeTax: applyEmployeeDeductions.value.incomeTax ? calculatedIncomeTax : 0,
    localTax: applyEmployeeDeductions.value.localTax ? calculatedLocalTax : 0,
    total: (applyEmployeeDeductions.value.incomeTax ? calculatedIncomeTax : 0) + (applyEmployeeDeductions.value.localTax ? calculatedLocalTax : 0)
  };
});

// 근로자 공제 합계
const totalEmployeeDeductions = computed(() => {
  return employeeDeductions.value.nationalPension +
         employeeDeductions.value.healthInsurance +
         employeeDeductions.value.longTermCare +
         employeeDeductions.value.employmentInsurance +
         tax.value.incomeTax +
         tax.value.localTax +
         employeeDeductions.value.supportDeduction;
});

// 기관 공제 합계
const totalCenterDeductions = computed(() => {
  return centerDeductions.value.nationalPension +
         centerDeductions.value.healthInsurance +
         centerDeductions.value.longTermCare +
         centerDeductions.value.employmentInsurance +
         centerDeductions.value.unemployment +
         centerDeductions.value.industrialAccident +
         centerDeductions.value.retirement;
});

// 실수령액 계산
const netPay = computed(() => {
  // 월 급여(기본급 + 주휴수당 + 연차수당) - 근로자 공제금액
  return Math.max(0, basicTotalPay.value - totalEmployeeDeductions.value);
});

// 금액 포맷팅 함수
const formatMoney = (amount: number) => {
  if (!amount) return "0";
  return new Intl.NumberFormat("ko-KR").format(amount);
};

// 퇴직금 적용 가능 여부에 따라 체크박스 자동 설정/해제
watch(canApplyRetirement, (newValue) => {
  if (!newValue && applyCenterDeductions.value.retirement) {
    // 60시간 미만이면 체크박스 해제
    applyCenterDeductions.value.retirement = false;
  } else if (newValue && !applyCenterDeductions.value.retirement) {
    // 60시간 이상이 되면 체크박스 자동 활성화
    applyCenterDeductions.value.retirement = true;
  }
});

// 토글 함수
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

// 크기 조절 시작
const startResize = (e: MouseEvent) => {
  isResizing.value = true;
  resizeStartY.value = e.clientY;
  resizeStartHeight.value = panelHeight.value;
  
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
  
  // 커서 변경 및 선택 방지
  document.body.style.cursor = 'ns-resize';
  document.body.style.userSelect = 'none';
  e.preventDefault();
};

// 크기 조절 중
const handleMouseMove = (e: MouseEvent) => {
  if (!isResizing.value) return;
  
  // 위로 드래그하면 패널이 커지도록 deltaY 계산 (부호 반대)
  const deltaY = resizeStartY.value - e.clientY;
  const newHeight = resizeStartHeight.value + deltaY;
  
  // 최소/최대 높이 제한 (90vh)
  const minHeight = 400;
  const maxHeight = window.innerHeight * 0.9;
  
  panelHeight.value = Math.max(minHeight, Math.min(maxHeight, newHeight));
};

// 크기 조절 종료
const handleMouseUp = () => {
  if (isResizing.value) {
    isResizing.value = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }
};

// 화면 크기 변경 시 패널 높이 조정
const handleWindowResize = () => {
  if (!isExpanded.value) return;
  
  const maxHeight = window.innerHeight * 0.9;
  const minHeight = 400;
  
  // 현재 패널 높이가 최대 높이를 초과하면 조정
  if (panelHeight.value > maxHeight) {
    panelHeight.value = maxHeight;
  }
  // 최소 높이보다 작으면 조정
  if (panelHeight.value < minHeight) {
    panelHeight.value = minHeight;
  }
};

// 컴포넌트 마운트 시 세금 데이터 로드
onMounted(async () => {
  await getTax();
  await loadHolidays();
  await loadSuga();
  // 초기 근무일수 기본값 20일 설정
  workingDaysInput.value = '20';
  
  // 화면 크기 변경 이벤트 리스너 추가
  window.addEventListener('resize', handleWindowResize);
});

// 컴포넌트 언마운트 시 이벤트 리스너 정리
onUnmounted(() => {
  handleMouseUp();
  window.removeEventListener('resize', handleWindowResize);
  stopContinuousIncrement(); // 연속 실행 타이머 정리
});
</script>

<style lang="scss" scoped>
@import '../../assets/styles/wageCalc.scss';
</style>
